import {
  AccountStatusEnum,
  IAccountTypenames,
  Query,
} from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useRef } from 'react';
import HasPlaidMarketingConsent from 'services/graphql/queries/Plaid/HasPlaidMarketingConsent';
import useExternalAccounts, {
  GroupedAccountsByInstitution,
} from 'shared/hooks/externalAccounts/useExternalAccounts';
import { ErrorScreenProps } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { RequestPolicy, useQuery } from 'urql';

export const PlaidMarketingConsentEntryPoint = {
  Home: 'home',
  LinkedAccounts: 'linkedAccounts',
  Transfer: 'transfer',
} as const;

export type PlaidMarketingConsentEntryPoint =
  (typeof PlaidMarketingConsentEntryPoint)[keyof typeof PlaidMarketingConsentEntryPoint];

interface UseHasPlaidMarketingConsentParams {
  entryPoint: PlaidMarketingConsentEntryPoint;
}

interface HasPlaidMarketingConsentResult {
  externalAccounts: GroupedAccountsByInstitution[];
  externalAccountsErrorModel: ErrorScreenProps | null;
  externalAccountsLoading: boolean;
  refreshExternalAccounts: () => void;
  hasPlaidMarketingConsent: boolean;
  fetching: boolean;
  refetch: (requestPolicy?: RequestPolicy) => void;
  startPlaidOrShowConsent: (startPlaid: () => void) => void;
}

interface PlaidAccountStatus {
  __typename?: string;
  status?: AccountStatusEnum | null;
}

export const hasActivePlaidAccount = (
  accounts: PlaidAccountStatus[] = [],
): boolean => {
  return accounts.some(
    (account) =>
      account.__typename === IAccountTypenames.ExternalPlaidAccount &&
      account.status === AccountStatusEnum.Active,
  );
};

const useHasPlaidMarketingConsent = ({
  entryPoint,
}: UseHasPlaidMarketingConsentParams): HasPlaidMarketingConsentResult => {
  const router = useRouter();
  const {
    errorModel: externalAccountsErrorModel,
    loading: externalAccountsLoading,
    response: externalAccounts,
    refresh: refreshExternalAccounts,
  } = useExternalAccounts();
  const [response, refetchData] = useQuery<Query>({
    query: HasPlaidMarketingConsent,
    requestPolicy: 'cache-and-network',
  });
  const hasFocusedBefore = useRef(false);

  const refetch = useCallback(
    (requestPolicy: RequestPolicy = 'network-only'): void => {
      refetchData({ requestPolicy });
    },
    [refetchData],
  );

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const hasPlaidMarketingConsent =
    response.data?.hasPlaidMarketingConsent ?? false;
  const fetching = externalAccountsLoading || response.fetching;
  const shouldShowPlaidMarketingConsent = !hasPlaidMarketingConsent;

  const startPlaidOrShowConsent = useCallback(
    (startPlaid: () => void) => {
      if (response.fetching) {
        return;
      }

      if (!shouldShowPlaidMarketingConsent) {
        startPlaid();
        return;
      }

      router.push({
        pathname: '/plaid/marketing-consent',
        params: {
          entryPoint,
        },
      });
    },
    [entryPoint, response.fetching, router, shouldShowPlaidMarketingConsent],
  );

  return {
    externalAccounts,
    externalAccountsErrorModel,
    externalAccountsLoading,
    refreshExternalAccounts,
    hasPlaidMarketingConsent,
    fetching,
    refetch,
    startPlaidOrShowConsent,
  };
};

export default useHasPlaidMarketingConsent;
